(() => {
  "use strict";

  if (window.HobProductGallery) return;

  const loaderScript = document.currentScript;
  const sources = {
    stylesheet: loaderScript?.dataset.galleryViewerStylesheet,
    lightbox: loaderScript?.dataset.galleryLightboxModule,
    core: loaderScript?.dataset.galleryCoreModule,
    video: loaderScript?.dataset.galleryVideoModule,
  };
  const labels = {
    close: loaderScript?.dataset.galleryCloseLabel || "Sluiten",
    previous: loaderScript?.dataset.galleryPreviousLabel || "Vorige afbeelding",
    next: loaderScript?.dataset.galleryNextLabel || "Volgende afbeelding",
  };
  const mountedInlineGalleries = new WeakMap();
  let stylesheetPromise;
  let viewerModulesPromise;
  let videoModulePromise;
  let activeViewer;
  let viewerOpening = false;
  let carouselsSuspended = false;

  function icon(path) {
    return `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="${path}"/></svg>`;
  }

  function suspendBackgroundCarousels() {
    if (carouselsSuspended || typeof window.HobCarousel?.suspend !== "function") return;
    window.HobCarousel.suspend("product-gallery");
    carouselsSuspended = true;
  }

  function resumeBackgroundCarousels() {
    if (!carouselsSuspended) return;
    window.HobCarousel?.resume?.("product-gallery");
    carouselsSuspended = false;
  }

  function loadViewerStylesheet() {
    if (stylesheetPromise) return stylesheetPromise;
    if (!sources.stylesheet) return Promise.reject(new Error("Product viewer stylesheet is missing"));

    stylesheetPromise = new Promise((resolve, reject) => {
      const absoluteSource = new URL(sources.stylesheet, window.location.href).href;
      const existing = Array.from(document.querySelectorAll('link[rel~="stylesheet"]'))
        .find((link) => link.href === absoluteSource);

      if (existing) {
        existing.media = "all";
        if (existing.sheet) resolve();
        else {
          existing.addEventListener("load", resolve, {once: true});
          existing.addEventListener("error", reject, {once: true});
        }
        return;
      }

      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = absoluteSource;
      link.dataset.hobProductViewerStylesheet = "true";
      link.addEventListener("load", resolve, {once: true});
      link.addEventListener("error", (event) => {
        link.remove();
        reject(event);
      }, {once: true});
      document.head.appendChild(link);
    }).catch((error) => {
      stylesheetPromise = undefined;
      throw error;
    });

    return stylesheetPromise;
  }

  function loadViewerModules() {
    if (viewerModulesPromise) return viewerModulesPromise;
    if (!sources.lightbox || !sources.core) {
      return Promise.reject(new Error("Product viewer modules are missing"));
    }

    viewerModulesPromise = Promise.all([
      import(sources.lightbox),
      import(sources.core),
    ]).then(([lightbox, core]) => ({
      PhotoSwipeLightbox: lightbox.default,
      PhotoSwipe: core.default,
    })).catch((error) => {
      viewerModulesPromise = undefined;
      throw error;
    });
    return viewerModulesPromise;
  }

  function loadVideoModule() {
    if (videoModulePromise) return videoModulePromise;
    if (!sources.video) return Promise.resolve(undefined);
    videoModulePromise = import(sources.video).then((module) => module.default).catch((error) => {
      videoModulePromise = undefined;
      throw error;
    });
    return videoModulePromise;
  }

  function warmViewer() {
    Promise.all([loadViewerStylesheet(), loadViewerModules()]).catch(() => {});
  }

  function makeArrow(className, label, path) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = className;
    button.setAttribute("aria-label", label);
    button.innerHTML = icon(path);
    return button;
  }

  function ensurePagination(swiper, pagination) {
    if (!pagination || pagination.children.length || !swiper.slides?.length) return;
    swiper.slides.forEach((_, index) => {
      const bullet = document.createElement("span");
      bullet.className = "swiper-pagination-bullet";
      if (index === swiper.activeIndex) bullet.classList.add("swiper-pagination-bullet-active");
      bullet.addEventListener("click", () => swiper.slideTo(index));
      pagination.appendChild(bullet);
    });
  }

  function updateInlineControls(gallery, swiper) {
    const previous = gallery.querySelector(".product-gallery-mobile-nav-prev");
    const next = gallery.querySelector(".product-gallery-mobile-nav-next");
    if (!previous || !next) return;

    previous.disabled = swiper.isBeginning;
    next.disabled = swiper.isEnd;
    previous.classList.toggle("disabled", swiper.isBeginning);
    next.classList.toggle("disabled", swiper.isEnd);
  }

  function ensureInlineControls(gallery, swiper) {
    const shouldShow = gallery.dataset.mobileGalleryArrows !== "false" && swiper.slides.length > 1;
    gallery.classList.toggle("mobile-gallery-arrows-hidden", !shouldShow);
    gallery.querySelectorAll(".product-gallery-mobile-nav-arrow").forEach((arrow) => {
      if (!shouldShow) arrow.remove();
    });
    if (!shouldShow) return;

    let previous = gallery.querySelector(".product-gallery-mobile-nav-prev");
    let next = gallery.querySelector(".product-gallery-mobile-nav-next");
    if (!previous) {
      previous = makeArrow(
        "product-gallery-mobile-nav-arrow product-gallery-mobile-nav-prev",
        labels.previous,
        "m14.5 5-7 7 7 7"
      );
      gallery.appendChild(previous);
    }
    if (!next) {
      next = makeArrow(
        "product-gallery-mobile-nav-arrow product-gallery-mobile-nav-next",
        labels.next,
        "m9.5 5 7 7-7 7"
      );
      gallery.appendChild(next);
    }
    previous.__hobProductGallerySwiper = swiper;
    next.__hobProductGallerySwiper = swiper;
    if (previous.dataset.hobProductGalleryBound !== "true") {
      previous.dataset.hobProductGalleryBound = "true";
      previous.addEventListener("click", () => previous.__hobProductGallerySwiper?.slidePrev());
    }
    if (next.dataset.hobProductGalleryBound !== "true") {
      next.dataset.hobProductGalleryBound = "true";
      next.addEventListener("click", () => next.__hobProductGallerySwiper?.slideNext());
    }
    updateInlineControls(gallery, swiper);
  }

  function mountInline(gallery) {
    if (!(gallery instanceof Element) || typeof window.Swiper !== "function") return undefined;

    const mounted = mountedInlineGalleries.get(gallery);
    if (mounted && !mounted.destroyed) {
      ensureInlineControls(gallery, mounted);
      return mounted;
    }
    if (gallery.swiper && !gallery.swiper.destroyed) {
      mountedInlineGalleries.set(gallery, gallery.swiper);
      ensureInlineControls(gallery, gallery.swiper);
      return gallery.swiper;
    }

    const pagination = gallery.querySelector(".product-gallery-mobile-pagination");
    const swiper = new window.Swiper(gallery, {
      slidesPerView: 1,
      longSwipesMs: 400,
      spaceBetween: 0,
      autoplay: false,
      loop: false,
      threshold: 5,
      effect: "slide",
      freeMode: false,
      cssMode: true,
      touchStartPreventDefault: false,
      touchMoveStopPropagation: false,
      allowTouchMove: true,
      touchEventsTarget: "container",
      watchSlidesProgress: false,
      pagination: pagination ? {
        clickable: true,
        el: pagination,
        type: "bullets",
        renderBullet: (_, className) => `<span class="${className}"></span>`,
      } : false,
      on: {
        init(instance) {
          ensurePagination(instance, pagination);
        },
      },
    });

    mountedInlineGalleries.set(gallery, swiper);
    ensureInlineControls(gallery, swiper);
    ["slideChange", "update", "reachBeginning", "reachEnd", "fromEdge"]
      .forEach((eventName) => swiper.on(eventName, () => updateInlineControls(gallery, swiper)));
    swiper.on("slidesLengthChange", () => ensureInlineControls(gallery, swiper));
    return swiper;
  }

  function getMediaElements(trigger) {
    const mediaList = trigger.closest(".product__media-list");
    if (!mediaList) return [];

    const activeMedia = mediaList.querySelectorAll(".media-active .bls__media-image");
    const mediaElements = activeMedia.length
      ? activeMedia
      : mediaList.querySelectorAll(".bls__media-image");
    const seenMedia = new Set();

    return Array.from(mediaElements).filter((media) => {
      const mediaId = media.dataset.mediaId || media.dataset.productMediaId || media.dataset.src;
      const supported = media.classList.contains("image")
        || media.classList.contains("video")
        || media.classList.contains("external_video");
      if (!supported || !mediaId || seenMedia.has(mediaId)) return false;
      seenMedia.add(mediaId);
      return true;
    });
  }

  function getVisibleImage(media) {
    const image = media.querySelector("img");
    const source = image?.currentSrc || image?.src;
    return source && !source.startsWith("data:image/gif") ? source : undefined;
  }

  function escapeAttribute(value) {
    return String(value || "")
      .replaceAll("&", "&amp;")
      .replaceAll('"', "&quot;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;");
  }

  function createItem(media) {
    const image = media.querySelector("img");
    const common = {
      mediaId: media.dataset.mediaId,
      width: Number(media.dataset.width) || Number(image?.width) || 1,
      height: Number(media.dataset.height) || Number(image?.height) || 1,
      msrc: getVisibleImage(media),
      alt: image?.alt || "",
    };

    if (media.classList.contains("video")) {
      return {...common, type: "video", videoSrc: media.dataset.sourceUrl};
    }
    if (media.classList.contains("external_video")) {
      const host = media.dataset.source;
      const videoId = encodeURIComponent(media.dataset.videoId || "");
      const videoSrc = host === "youtube"
        ? `https://www.youtube-nocookie.com/embed/${videoId}`
        : `https://player.vimeo.com/video/${videoId}`;
      const title = escapeAttribute(common.alt || "Productvideo");
      return {
        ...common,
        type: "html",
        html: `<div class="hob-product-lightbox__external-video"><iframe src="${videoSrc}" title="${title}" allow="autoplay; encrypted-media; picture-in-picture" allowfullscreen></iframe></div>`,
      };
    }

    const pictureSource = media.querySelector("picture source");
    return {
      ...common,
      src: image?.dataset.zoom || media.dataset.src,
      srcset: pictureSource?.srcset || pictureSource?.dataset.srcset,
    };
  }

  function getTriggerMediaId(trigger) {
    if (trigger.dataset.productLightboxMediaId) return trigger.dataset.productLightboxMediaId;
    const owner = trigger.closest(".gallery-img, .swiper-slide");
    if (owner) return owner.querySelector(".bls__media-image")?.dataset.mediaId;

    const mediaList = trigger.closest(".product__media-list");
    return mediaList?.querySelector(".swiper-slide-active .bls__media-image")?.dataset.mediaId
      || mediaList?.querySelector(".media-active .bls__media-image")?.dataset.mediaId
      || mediaList?.querySelector(".bls__media-image")?.dataset.mediaId;
  }

  function registerViewerUi(lightbox) {
    lightbox.on("uiRegister", () => {
      const pswp = lightbox.pswp;
      pswp.ui.registerElement({
        name: "hob-close",
        isButton: true,
        tagName: "button",
        ariaLabel: labels.close,
        html: icon("M6 6l12 12M18 6 6 18"),
        onClick: () => pswp.close(),
      });

      pswp.ui.registerElement({
        name: "hob-controls",
        className: "pswp__hob-controls",
        appendTo: "wrapper",
        onInit: (controls) => {
          const previous = document.createElement("button");
          previous.type = "button";
          previous.className = "pswp__hob-control pswp__hob-control--previous";
          previous.setAttribute("aria-label", labels.previous);
          previous.innerHTML = icon("m14.5 5-7 7 7 7");
          previous.addEventListener("click", () => pswp.prev());

          const counter = document.createElement("span");
          counter.className = "pswp__hob-counter";
          counter.setAttribute("aria-live", "polite");

          const next = document.createElement("button");
          next.type = "button";
          next.className = "pswp__hob-control pswp__hob-control--next";
          next.setAttribute("aria-label", labels.next);
          next.innerHTML = icon("m9.5 5 7 7-7 7");
          next.addEventListener("click", () => pswp.next());

          const update = () => {
            const index = pswp.currIndex;
            const count = pswp.getNumItems();
            counter.textContent = `${index + 1} / ${count}`;
            previous.disabled = index === 0;
            next.disabled = index === count - 1;
          };

          controls.append(previous, counter, next);
          pswp.on("change", update);
          update();
        },
      });
    });
  }

  async function openViewer(trigger) {
    if (activeViewer || viewerOpening || window.pswp) return;

    const items = getMediaElements(trigger).map(createItem).filter((item) => item.src || item.videoSrc || item.html);
    if (!items.length) return;

    const requestedMediaId = getTriggerMediaId(trigger);
    const startIndex = Math.max(0, items.findIndex((item) => item.mediaId === requestedMediaId));
    const hasNativeVideo = items.some((item) => item.type === "video");
    viewerOpening = true;

    try {
      const [_, modules, PhotoSwipeVideoPlugin] = await Promise.all([
        loadViewerStylesheet(),
        loadViewerModules(),
        hasNativeVideo ? loadVideoModule() : Promise.resolve(undefined),
      ]);
      const {PhotoSwipeLightbox, PhotoSwipe} = modules;
      const lightbox = new PhotoSwipeLightbox({
        dataSource: items,
        pswpModule: PhotoSwipe,
        mainClass: "hob-product-lightbox",
        loop: false,
        allowPanToNext: false,
        pinchToClose: false,
        closeOnVerticalDrag: true,
        clickToCloseNonZoomable: false,
        imageClickAction: "zoom",
        bgClickAction: "close",
        tapAction: "toggle-controls",
        doubleTapAction: "zoom",
        initialZoomLevel: "fit",
        secondaryZoomLevel: 2.5,
        maxZoomLevel: 3,
        wheelToZoom: false,
        spacing: 0.04,
        preload: [1, 1],
        counter: false,
        zoom: false,
        arrowPrev: false,
        arrowNext: false,
        close: false,
        preloader: true,
        bgOpacity: 1,
        showHideAnimationType: "fade",
        showAnimationDuration: 180,
        hideAnimationDuration: 160,
        zoomAnimationDuration: 220,
        paddingFn: (viewportSize) => viewportSize.x < 992
          ? {top: 68, bottom: 86, left: 12, right: 12}
          : {top: 76, bottom: 92, left: 72, right: 72},
      });

      registerViewerUi(lightbox);
      if (PhotoSwipeVideoPlugin) {
        new PhotoSwipeVideoPlugin(lightbox, {
          autoplay: false,
          videoAttributes: {controls: "", playsinline: "", preload: "metadata"},
        });
      }
      lightbox.on("afterInit", () => {
        viewerOpening = false;
      });
      lightbox.on("destroy", () => {
        activeViewer = undefined;
        viewerOpening = false;
        resumeBackgroundCarousels();
      });

      lightbox.init();
      activeViewer = lightbox;
      suspendBackgroundCarousels();
      if (!lightbox.loadAndOpen(startIndex)) {
        activeViewer = undefined;
        viewerOpening = false;
        resumeBackgroundCarousels();
      }
    } catch (error) {
      activeViewer = undefined;
      viewerOpening = false;
      resumeBackgroundCarousels();
      throw error;
    }
  }

  function findTrigger(target) {
    return target instanceof Element ? target.closest("[data-product-lightbox-trigger]") : null;
  }

  document.addEventListener("pointerover", (event) => {
    if (event.pointerType === "mouse" && findTrigger(event.target)) warmViewer();
  }, {passive: true, capture: true});

  document.addEventListener("focusin", (event) => {
    const trigger = findTrigger(event.target);
    if (trigger?.matches(":focus-visible")) warmViewer();
  }, true);

  document.addEventListener("click", (event) => {
    const trigger = findTrigger(event.target);
    if (!trigger) return;
    const inlineSwiper = trigger.closest(".bls__swiper-gallery-mobile")?.swiper;
    if (inlineSwiper && inlineSwiper.allowClick === false) return;
    event.preventDefault();
    openViewer(trigger).catch((error) => {
      console.error("[Product Gallery] Viewer failed to open", error);
    });
  });

  document.addEventListener("keydown", (event) => {
    const trigger = findTrigger(event.target);
    if (!trigger || trigger.tagName === "BUTTON") return;
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    warmViewer();
    openViewer(trigger).catch((error) => {
      console.error("[Product Gallery] Viewer failed to open", error);
    });
  });

  window.HobProductGallery = Object.freeze({mountInline});
  document.dispatchEvent(new CustomEvent("hob:product-gallery-ready"));
})();
